import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { Text } from '@visx/text';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import {
  getParams,
  thousandFormat,
  printFormat,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';
import appdata from '../../../../Services/appdata';

let tooltipTimeout;

const ByChart = ({
  summaryData,
  period,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  selectedMiddleItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const isMountedRef = useRef(false);

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
  } = useTooltip();

  const [state, setState] = useState({
    resize: false,
    hoverBar: null,
    selectedBars: [],
    data: [],
    incomes: [],
    totals: [],
    forecasts: [],
    colors: [],
  });

  const onResize = useCallback(() => {
    if (isMountedRef.current === true) {
      setState(prevState => ({ ...prevState, resize: !prevState.resize }));
    }
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictForecasts = {};
    let data = [];
    let totals = [];
    let forecasts = [];
    let incomes = [];

    let colors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    if (summaryData['byType'])
      summaryData['byType'].forEach((item) => {
        let isInFilter = false;
        if (selectedTopItems.length === 0) {
          isInFilter = true;
        } else {
          selectedTopItems.forEach((topitem) => {
            if (parseInt(topitem.year) === item.FY) {
              if (parseInt(topitem.month) === undefined) {
                isInFilter = true;
              } else {
                let selYear = parseInt(topitem.year);
                let selYearMonth = '';
                if (parseInt(topitem.month) >= defaultStartMonth - 1)
                  selYear -= 1;
                selYearMonth = printFormat.dateWithHyphens(
                  selYear,
                  parseInt(topitem.month) + 1,
                );

                if (selYearMonth === item.Date.slice(0, 10)) {
                  isInFilter = true;
                }
              }
            }
          });
        }
        if (
          isInFilter &&
          item.AccountType !== 'Bonuses' &&
          item.AccountType !== 'Other Income' &&
          item.AccountType !== 'Extra Ordinary'
        ) {
          if (!dictData[item.AccountType]) {
            dictData[item.AccountType] = {};
            dictData[item.AccountType]['data'] = item.AccountType;
            dictData[item.AccountType]['year'] = parseInt(item.FY);
            dictData[item.AccountType]['ActualExpenses'] = 0;
            dictData[item.AccountType]['ForecastExpenses'] = 0;

            dictTotals[item.AccountType] = 0;
            dictForecasts[item.AccountType] = 0;
          }

          dictData[item.AccountType]['ActualExpenses'] += parseFloat(
            item.ActualExpenses,
          );
          dictData[item.AccountType]['ForecastExpenses'] += parseFloat(
            item.ForecastExpenses,
          );

          dictTotals[item.AccountType] += parseFloat(item.ActualExpenses);
          dictForecasts[item.AccountType] += parseFloat(item.ForecastExpenses);
        }
      });

    Object.keys(dictData).map((item) => data.push(dictData[item]));

    Object.keys(dictTotals).map((item) => totals.push(dictTotals[item]));

    Object.keys(dictForecasts).map((item) =>
      forecasts.push(dictForecasts[item]),
    );

    if (summaryData['income']) {
      summaryData['income'].forEach((item) => {
        let obj_data = {
          month: Math.floor((item.DimDate % 10000) / 100),
          value: item.Income,
        };
        incomes.push(obj_data);
      });
    }

    setState(prevState => ({
      ...prevState,
      data: data,
      totals: totals,
      forecasts: forecasts,
      colors: colors,
      incomes,
    }));
  }, [summaryData, selectedYears, selectedTopItems, defaultStartMonth]);

  const getColor = useCallback((bar) => {
    const subtype = bar.bar.data.data;

    const actual = bar.bar.data.ActualExpenses
      ? bar.bar.data.ActualExpenses
      : 0;
    const forecast = bar.bar.data.ForecastExpenses
      ? bar.bar.data.ForecastExpenses
      : 0;

    const activeColor =
      parseFloat(actual) > parseFloat(forecast)
        ? negativeActiveColor
        : positiveActiveColor;
    const disableColor =
      parseFloat(actual) > parseFloat(forecast)
        ? negativeDisableColor
        : positiveDisableColor;

    if (selectedMiddleItems.length === 0) {
      return activeColor;
    }

    for (let i = 0; i < selectedMiddleItems.length; i++) {
      if (selectedMiddleItems[i] === subtype) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedMiddleItems]);

  const handleBar = useCallback((event, bar) => {
    const { selectedBars } = state;

    let _selectedBars, _selectedMiddleItems;

    const subtype = bar.bar.data.data;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMiddleItems = selectedMiddleItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedMiddleItems.push(subtype);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedMiddleItems.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedMiddleItems = [];
        _selectedBars = [];
      } else {
        _selectedMiddleItems = [subtype];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setState(prevState => ({
      ...prevState,
      selectedBars: _selectedBars,
    }));

    handleFilter({
      selectedMiddleItems: _selectedMiddleItems,
    });
  }, [state.selectedBars, selectedMiddleItems, handleFilter]);

  const handleLabel = useCallback((event, data) => {
    const { selectedBars } = state;
    let _selectedMiddleItems, _selectedBars;

    let index = NaN;
    for (let i = 0; i < selectedMiddleItems.length; i++) {
      if (selectedMiddleItems[i] === data) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMiddleItems = selectedMiddleItems.slice();

      if (isNaN(index)) {
        _selectedMiddleItems.push(data);
      } else {
        _selectedMiddleItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedMiddleItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedMiddleItems.length === 1) {
        _selectedMiddleItems = [];
      } else {
        _selectedMiddleItems = [data];
      }
    }

    setState(prevState => ({
      ...prevState,
      selectedBars: _selectedBars,
    }));

    handleFilter({
      selectedMiddleItems: _selectedMiddleItems,
    });
  }, [state.selectedBars, selectedMiddleItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    const { hoverBar, selectedBars } = state;

    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setState(prevState => ({
      ...prevState,
      hoverBar: null,
      selectedBars: [],
    }));
    localStorage.removeItem('expenseMiddleItems');
    handleFilter({
      selectedMiddleItems: [],
    });
  }, [state, handleFilter]);

  const handlePos = useCallback((event) => {
    const { pageOffset } = state;
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (pageOffset !== _pos && isMountedRef.current === true) {
      setState(prevState => ({ ...prevState, pageOffset: _pos }));
    }
  }, [state]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {};

    const coords = localPoint(event.target.ownerSVGElement, event);

    if (isBar) {
      const { hoverBar } = state;
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setState(prevState => ({ ...prevState, hoverBar: event.target }));
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    // calculate mtdSum
    let selSum = 0;
    let mtdMonths = 0;

    if (state.data && selectedMiddleItems.length > 1)
      state.data.forEach((item) => {
        selectedMiddleItems.forEach((type) => {
          if (item.data === type) {
            selSum += item.ActualExpenses;
            mtdMonths++;
          }
        });
      });

    data['selSum'] = selSum;
    data['items'] = mtdMonths;

    showTooltip({
      tooltipData: data,
      tooltipTop: coords.y + 200,
      tooltipLeft: coords.x,
    });
  }, [state, selectedMiddleItems, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    const { hoverBar } = state;

    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setState(prevState => ({ ...prevState, hoverBar: null }));

      hideTooltip();
    }, 300);
  }, [state, hideTooltip]);

  useEffect(() => {
    isMountedRef.current = true;
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  useEffect(() => {
    if (isMountedRef.current === true) {
      prepareData();
    }
  }, [summaryData, period, selectedTopItems, prepareData]);

  const { data, incomes, totals, forecasts, colors } = state;

  let actual_income = 0;
  if (selectedMonths.length > 0) {
    const filteredIncomes = incomes.filter(
      (item) => selectedMonths.indexOf(item.month - 1) !== -1,
    );
    filteredIncomes.forEach((item) => (actual_income += parseInt(item.value)));
  }

  let xTransform = 0;
  let maxYValue = Math.max(Math.max(...totals), Math.max(...forecasts));
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7) {
      xTransform = 7.7 * (thousandFormat2(maxYValue).length + 1) + 5;
    } else xTransform = 7.7 * thousandFormat2(maxYValue).length + 5;
  }
  appdata.setLocalItem('expenseByChartxTransform', xTransform);
  const width = window.innerWidth - 16;
  const height = window.innerHeight / 6.5;
  const margin = {
    top: 7,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = 3.5;
  const offsetWidth = -1.2;

  // accessors
  const x = (d) => d.data;

  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .padding(0.2)
    .rangeRound([0, width - xTransform]);

  let totalmax = Math.max(0, Math.max(...totals), Math.max(...forecasts));
  const yScale = scaleLinear({
    domain: [0, totalmax * 1.25],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  data.sort((a, b) => a.data && b.data && a.data[0] - b.data[0]);
  let keys = [];
  keys.push('ActualExpenses');

  return (
    <div
      id="expense-by-chart"
      style={{ height: 180 }}
      onMouseMove={(event) => handlePos(event)}
      onTouchMove={(event) => handlePos(event)}
    >
      <div className="subtitle mb-10">By Type</div>

      <Grid container>
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          <Group top={margin.top} left={margin.left}>
            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />
            <BarStack
              data={data}
              incomes={incomes}
              keys={keys}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar, key) => {
                    if (bar.height < 0) bar.height = 0;
                    return (
                      <Group
                        key={`bar-income-${barStack.index}-${bar.index}`}
                      >
                        <rect
                          x={bar.x}
                          y={bar.y}
                          height={isNaN(bar.height) ? 0 : bar.height}
                          width={isNaN(bar.width) ? 0 : bar.width}
                          fill={getColor(bar)}
                          onClick={(event) => handleBar(event, bar)}
                          onMouseLeave={(event) => hideTooltipHandler()}
                          onMouseMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                          onTouchEnd={(event) => hideTooltipHandler()}
                          onTouchMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                        />

                        <Text
                          x={bar.x + bar.width / 2}
                          y={
                            bar.bar.data.ForecastExpenses
                              ? yScale(bar.bar.data.ForecastExpenses) - 3
                              : yScale(0)
                          }
                          verticalAnchor="end"
                          textAnchor="start"
                          fontSize={11}
                          dx={-5}
                          dy={-5}
                        >
                          {selectedMonths.length > 1
                            ? printFormat.percentage(
                                isFinite(actual_income) && !isNaN(actual_income) && actual_income !== 0
                                  ? Math.round((bar.bar.data.ActualExpenses * 100) / actual_income)
                                  : 0
                              )
                            : (() => {
                                const incomeValue = incomes.find(
                                  (item) => item.month === selectedMonths[0] + 1
                                )?.value || incomes.find((item) =>
                                  defaultMonth < defaultStartMonth
                                    ? item.month === defaultMonth + defaultStartMonth - 1
                                    : item.month === defaultMonth - defaultStartMonth + 1
                                )?.value || 0;
                                
                                return printFormat.percentage(
                                  isFinite(incomeValue) && !isNaN(incomeValue) && incomeValue !== 0
                                    ? Math.round((bar.bar.data.ActualExpenses * 100) / incomeValue)
                                    : 0
                                );
                              })()}
                        </Text>
                      </Group>
                    );
                  });
                });
              }}
            </BarStack>

            {forecasts.map((forecast, index) => {
              return (
                <Group key={`bar-forecast-${index}`}>
                  <Bar
                    width={
                      (xMax - xScale.paddingInner() * xScale.step()) /
                        forecasts.length +
                      offsetWidth
                    }
                    height={1}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step() +
                      offsetX
                    }
                    y={yScale(forecast)}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={(event) => hideTooltipHandler()}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                    onTouchEnd={(event) => hideTooltipHandler()}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, xScale, forecast, false)
                    }
                  />
                </Group>
              );
            })}

            {data.map(x).map((oneItem, index) => {
              return (
                <rect
                  key={index}
                  x={
                    (xScale.paddingInner() * xScale.step()) / 2 +
                    index * xScale.step() +
                    offsetX
                  }
                  y={yMax}
                  width={
                    (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                      data.map(x).length +
                    offsetWidth
                  }
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) => handleLabel(event, oneItem)}
                />
              );
            })}

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  style={{
                    fontWeight:
                      selectedMiddleItems.indexOf(formattedValue) > -1
                        ? 'bold'
                        : 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  fill={
                    selectedMiddleItems.indexOf(formattedValue) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabel(event, formattedValue)
                  }
                >
                  {formattedValue.length > 20
                    ? formattedValue.substring(0, 18) + '...'
                    : formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="color-black" style={{ padding: 0, margin: 0 }}>
                {tooltipData.selSum > 0 ? (
                  <div
                    className="ft-12"
                    style={{
                      backgroundColor: '#dddddd',
                      margin: 0,
                      minHeight: 25,
                    }}
                  >
                    <div style={{ padding: 5 }}>
                      {tooltipData.items} items selected
                      SUM(ChangeDuringPeriod_HistoricalOnly):&nbsp;
                      <strong>{thousandFormat2(tooltipData.selSum)}</strong>
                    </div>
                  </div>
                ) : (
                  ''
                )}
                <div style={{ margin: 5 }}>
                  <div className="pdv-10">
                    <strong>{tooltipData.bar.data.data}</strong>
                  </div>
                  <div className="ft-12">
                    Actual:{' '}
                    <strong>
                      ${thousandFormat(tooltipData.bar.data.ActualExpenses)}
                    </strong>
                  </div>
                  <div className="ft-12">
                    Forecast:{' '}
                    <strong>
                      ${thousandFormat(tooltipData.bar.data.ForecastExpenses)}
                    </strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="color-black" style={{ margin: 5 }}>
                Sum ForecastMonth HistoricalOnly = $
                {thousandFormat(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

ByChart.propTypes = {
  summaryData: PropTypes.object.isRequired,
  period: PropTypes.string.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
  defaultMonth: PropTypes.number.isRequired,
};

export default ByChart;
